import type { NodeHandle } from './RuntimeHandles';
import { RuntimeBindings } from './RuntimeBindings';
import { MAX_LOWERED_GRAPH_ITEMS } from './graphBudgets';
import { LogicGraph } from './LogicGraph';
import {
  hasNativeRuntimeIdentity,
  hasNativeRuntimeIdentityHooks,
} from './LogicNodeInstance';
import {
  RuntimeIdentityTokens,
  RuntimeScopeBuilder,
  runtimeIdentity,
} from './RuntimeIdentity';
import type { IChipInstanceModel, ILogicGraph } from '@/interfaces/models';

/** Preserve each placed chip's state without sharing it with another instance. */
export function restoreInstanceRuntime(
  instance: IChipInstanceModel,
  graph: ILogicGraph,
  idMap: Map<NodeHandle, NodeHandle>,
  previousIds: Map<NodeHandle, NodeHandle> = instance.templateToInstanceIdMap,
) {
  const previous = instance.logicGraph;
  const hooks = hasNativeRuntimeIdentityHooks();
  let native = hooks;
  for (const nodes of [previous.nodes, graph.nodes])
    for (const node of nodes.values())
      if (!hasNativeRuntimeIdentity(node, hooks)) native = false;
  function* identities() {
    for (const nodes of [previous.nodes, graph.nodes])
      for (const node of nodes.values()) yield node.runtimeIdentity;
  }
  const tokens = native ? new RuntimeIdentityTokens(identities()) : undefined;
  const key = (node: import('./LogicNodeInstance').LogicNodeInstance) => {
    if (!tokens) return node.runtimeKey;
    const identity = node.runtimeIdentity;
    return identity !== undefined && identity !== '' ? tokens.token(identity) : undefined;
  };
  const byKey = new Map<
    string | number,
    import('./LogicNodeInstance').LogicNodeInstance
  >();
  for (const node of previous.nodes.values()) if (key(node)) byKey.set(key(node)!, node);
  instance.templateToInstanceIdMap.resolveTemplate(instance.chip?.templateGraph);
  const scope = native ? new RuntimeScopeBuilder(0, instance.id) : undefined;
  for (const node of graph.nodes.values()) {
    if (scope) {
      const identity = node.runtimeIdentity;
      if (identity !== undefined && identity !== '')
        node.runtimeIdentity = scope.wrap(identity);
    } else if (node.runtimeKey)
      node.runtimeKey = `chip:${instance.id.length}:${instance.id}${node.runtimeKey}`;
  }

  const legacyNodes = byKey.size === 0 ? matchLegacyNodes(previous, graph) : undefined;

  for (const [templateId, newId] of idMap) {
    const node = graph.nodes.get(newId);
    if (!node) continue;
    const oldId = previousIds.get(templateId);
    const oldNode =
      (key(node) && byKey.get(key(node)!)) ||
      (oldId && previous.nodes.get(oldId)) ||
      legacyNodes?.get(newId);
    if (oldNode) node.restoreRuntimeState(oldNode);
  }
}

/** Older snapshots preserve clone order. Validate the complete correspondence. */
function matchLegacyNodes(previous: ILogicGraph, next: ILogicGraph) {
  const oldNodes = [...previous.nodes.values()];
  const newNodes = [...next.nodes.values()];
  if (
    oldNodes.length !== newNodes.length ||
    previous.edges.size !== next.edges.size ||
    !oldNodes.every((node, index) => node.type === newNodes[index].type)
  )
    return;
  const oldToNew = new Map(oldNodes.map((node, index) => [node.id, newNodes[index].id]));
  const edgeKey = (
    source: NodeHandle | undefined,
    target: NodeHandle | undefined,
    meta: unknown,
  ) => JSON.stringify([source, target, meta ?? null]);
  const newEdges = new Set(
    [...next.edges.values()].map((edge) =>
      edgeKey(edge.sourceNodeId, edge.targetNodeId, edge.meta),
    ),
  );
  if (
    ![...previous.edges.values()].every((edge) =>
      newEdges.has(
        edgeKey(
          oldToNew.get(edge.sourceNodeId),
          oldToNew.get(edge.targetNodeId),
          edge.meta,
        ),
      ),
    )
  )
    return;
  return new Map(newNodes.map((node, index) => [node.id, oldNodes[index]]));
}

/** One independently restored authored graph per lane, with stable nested identities. */
export function cloneLiftedInstance(
  instance: IChipInstanceModel,
  template: ILogicGraph,
  width: number,
) {
  const graph = new LogicGraph(template.ids);
  const laneMaps: Map<NodeHandle, NodeHandle>[] = [];
  const combinedMap = new RuntimeBindings();
  const nativeHooks = hasNativeRuntimeIdentityHooks();
  for (let lane = 0; lane < width; lane++) {
    const clone = template.clone();
    const scope = new RuntimeScopeBuilder(1, lane);
    for (const node of clone.graph.nodes.values())
      if (hasNativeRuntimeIdentity(node, nativeHooks))
        node.runtimeIdentity = scope.wrap(
          node.runtimeIdentity ?? runtimeIdentity(node.idText),
        );
      else
        node.runtimeKey = JSON.stringify(['lift', lane, node.runtimeKey ?? node.idText]);
    instance.templateToInstanceIdMap.resolveTemplate(template);
    restoreInstanceRuntime(
      instance,
      clone.graph,
      clone.idMap,
      instance.templateToInstanceIdMap.lanes.get(lane) ?? new Map(),
    );
    graph.mergeFrom(clone.graph);
    laneMaps.push(clone.idMap);
    combinedMap.lanes.set(lane, clone.idMap);
  }
  return { graph, laneMaps, combinedMap };
}

export function validateChipLift(
  template: ILogicGraph | undefined,
  width: number,
): string | undefined {
  if (!Number.isInteger(width) || width < 1 || width > 4096)
    return 'Authored logic lane width must be an integer from 1 to 4096';
  if (!template) return 'Authored chip logic must be loaded before lifting';
  const cost = width * (template.nodes.size + template.edges.size);
  if (cost > MAX_LOWERED_GRAPH_ITEMS)
    return `Authored lane lift requires ${cost} graph nodes and edges; the limit is 200000`;
  return undefined;
}
